import { createFileRoute, useRouter } from '@tanstack/react-router';
import { lazy, Suspense, useEffect } from 'react';
import { z } from 'zod';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { CostGatePanel } from '~/features/catalog-enrichment/components/cost-gate-panel';
import { useRunProgress } from '~/features/catalog-enrichment/hooks/use-run-progress';
import { gateRedirectSearch } from '~/features/catalog-enrichment/utils/run-decisions';
import {
  runProductsSearchSchema,
  type RunProductsSearch,
} from '~/features/catalog-enrichment/utils/run-products-search';

const RunProductsPage = lazy(() => import('~/features/catalog-enrichment/pages/run-products-page'));

/** The Review tab's search: the products list's own params, plus the old `/gate?productId=` key.
 *  (`stage` is the list's own filter here; an old `?stage=<gate>` falls back to the open gate.) */
const gateSearchSchema = runProductsSearchSchema.extend({
  productId: z.string().optional().catch(undefined),
});

/**
 * The batch's Review tab (design guide part 2 §2.5): where the decision the batch waits on is
 * made — a cost approval's panel, then the open gate's decision panel above the products that
 * need a look. Products stays a plain inspection list. Old links (`?stage=`, `?productId=`, a
 * Slack/email "review" link) land here and are expanded into the review lens once.
 */
export const Route = createFileRoute('/_authenticated/_tenant/catalog-enrichment/runs/$runId/gate')(
  {
    validateSearch: gateSearchSchema,
    component: RouteComponent,
  },
);

/** True while the browser is on a batch's Review route. */
function isReviewPath(pathname: string): boolean {
  return pathname.replace(/\/$/, '').endsWith('/gate');
}

function RouteComponent() {
  const { runId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();
  const progress = useRunProgress(runId);
  // A search-only navigate is bound to this route (`from: …/gate`): fired after the user left
  // for another tab (this component is still mounted mid-transition, reading the new location's
  // search), it would pull them straight back to Review. Only ever update while still here.
  const stillHere = () => isReviewPath(router.state.location.pathname);
  const openStage = progress.data?.pendingGate?.stage ?? null;
  const needsSeed = !search.decision && !search.lens && !!progress.data?.pendingGate;

  // First arrival: open the review lens on the gate the link named (or the open one).
  useEffect(() => {
    if (!needsSeed || !isReviewPath(router.state.location.pathname)) return;
    const seed = gateRedirectSearch(null, search.productId ?? null, openStage);
    void navigate({
      search: (prev) => ({ ...seed, ...prev, productId: undefined }),
      replace: true,
    });
  }, [needsSeed, search.productId, openStage, navigate, router]);

  // Filter/page changes replace the history entry so Back leaves the screen, not the filter.
  const onSearchChange = (patch: Partial<RunProductsSearch>) => {
    if (!stillHere()) return;
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };

  if (progress.isLoading) return <PageSkeleton />;
  const costGate = progress.data?.costGate ?? null;

  return (
    <div className="flex flex-col gap-4" data-testid="catalog-enrichment-run-review-page">
      {costGate ? <CostGatePanel runId={runId} waiting={costGate} /> : null}
      {costGate && !progress.data?.pendingGate ? null : (
        <Suspense fallback={<PageSkeleton />}>
          <RunProductsPage
            key={runId}
            runId={runId}
            search={search}
            onSearchChange={onSearchChange}
            mode="review"
          />
        </Suspense>
      )}
    </div>
  );
}
